import React, { useEffect, useMemo, useState, ReactNode } from "react";

// UX
import CardAddNedDrugOrderItemUX from "./CardAddNedDrugOrderItemUX";

// Common
import { Checkbox } from "semantic-ui-react";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DropDownColumn from "react-lib/appcon/common/DropDownColumn";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { dateToStringBE } from "react-lib/utils/dateUtils";

// * Serializer
import DrugOrderItemNedSerializer from "issara-sdk/types/DrugOrderItemNedSerializer_apps_TPD";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SelectedItemsType,
  FilterSearchType,
  TableHeaderType,
} from "./sequence/AddNedDrugOrderItem";

// Type
type CardAddNedDrugOrderItemProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  AddNedDrugOrderItemSequence: State["AddNedDrugOrderItemSequence"];
  languageUX: any;
} & PickedProps;

const CardAddNedDrugOrderItem = (props: CardAddNedDrugOrderItemProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItemsType[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "AddNedDrugOrderItem", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  // isChecked
  const isChecked = (id: number) => selectedItems.some((s) => s.id === id);

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      const newData = (props.AddNedDrugOrderItemSequence?.drugOrderList || []).map(
        (item: DrugOrderItemNedSerializer) => ({
          id: item.id,
          ned: item.ned, //*
        })
      );

      setSelectedItems(newData);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "AddNedDrugOrderItem",
      action: "SEARCH",
      page: 0,
    });

    setSelectedItems([]);
  };

  const handleSave = () => {
    props.runSequence({
      sequence: "AddNedDrugOrderItem",
      action: "SAVE",
      selectedItems,
    });

    setSelectedItems([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "AddNedDrugOrderItem",
      action: "CLEAR",
    });

    setSelectedItems([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;

    props.setProp(`AddNedDrugOrderItemSequence.searchDrugOrder.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`AddNedDrugOrderItemSequence.searchDrugOrder.create_date`, date);
  };

  const handleChangeEdit = (id: number, key: keyof SelectedItemsType, value: any) => {
    setSelectedItems((prev) => {
      const exists = prev.find((s) => s.id === id);

      if (exists) {
        return prev.map((s) => (s.id === id ? { ...s, [key]: value } : s));
      } else {
        const originalItem = props.AddNedDrugOrderItemSequence?.drugOrderList?.find(
          (i) => i.id === id
        );

        if (!originalItem) return prev;

        return [
          ...prev,
          {
            ...originalItem,
            [key]: value,
          },
        ];
      }
    });
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`AddNedDrugOrderItemSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "AddNedDrugOrderItem",
      action: "SEARCH",
      [key]: value,
    });

    setSelectedItems([]);
    props.setProp(`AddNedDrugOrderItemSequence.selectedItems`, []);
  };

  const handleDisabledSave = useMemo(() => {
    if (selectedItems.length === 0) return true;
  }, [selectedItems, props.AddNedDrugOrderItemSequence?.drugOrderList]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  // EditorColumn Usage
  const renderEditorColumn = (
    id: number,
    field: keyof SelectedItemsType,
    value: any,
    options: any[]
  ) => (
    <DropDownColumn
      options={options}
      value={value}
      showText={value}
      readOnly={!isChecked(id) || value === "-"} // * ไม่สามารถเลือก NED ที่เป็น "-" ได้
      onDataChanged={(v: any) => handleChangeEdit(id, field, v)}
      backgroundColor={isChecked(id) ? "rgb(255, 255, 204)" : ""}
      style={{ width: "100%" }}
    />
  );

  const drugOrderTypeOptions = useMemo(() => {
    const options = props.AddNedDrugOrderItemSequence?.drugOrderTypeOptions || [];

    return [
      ...options.map((item) => ({
        key: item.id,
        value: item.value,
        text: item.value,
      }))
    ];
  }, [props.AddNedDrugOrderItemSequence?.drugOrderTypeOptions]);

  const drugOrderList = useMemo(() => {
    return (props.AddNedDrugOrderItemSequence?.drugOrderList || []).map(
      (item: DrugOrderItemNedSerializer, index: number) => {
        const nedOptions = props.masterOptions?.ned || [];
        const selectedRow = selectedItems.find((s) => s.id === item.id);
        const rawNed = selectedRow?.ned || item.ned;
        const rawNedValue = nedOptions?.find((opt: any) => opt?.text === item.ned)?.value ?? item.ned;
        const nedValue = nedOptions?.find((opt: any) => opt?.text === rawNed)?.value ?? rawNed;

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={isChecked(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  const exists = selectedItems.find((s) => s.id === item.id);

                  if (!exists) {
                    setSelectedItems([
                      ...selectedItems,
                      {
                        id: item.id,
                        ned: rawNedValue,
                      },
                    ]);
                  }
                } else {
                  setSelectedItems(selectedItems.filter((s) => s.id !== item.id));
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof DrugOrderItemNedSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              // * Format
              if (["drug_name", "ned"].includes(key)) {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
          ned: renderEditorColumn(
            item.id!,
            "ned",
            nedValue,
            nedOptions || []
          ),
        };
      }
    );
  }, [
    selectedItems,
    tableHeaders,
    props.AddNedDrugOrderItemSequence?.drugOrderList,
    props.masterOptions?.ned,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardAddNedDrugOrderItemUX
        onChangeDate={handleChangeDate}
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchDrugOrder={props.AddNedDrugOrderItemSequence?.searchDrugOrder}
        // dropdown
        orderTypeOptions={drugOrderTypeOptions || []}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedItems.length ===
                    (props.AddNedDrugOrderItemSequence?.drugOrderList || []).length &&
                    (props.AddNedDrugOrderItemSequence?.drugOrderList || []).length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        drugOrderList={drugOrderList}
        paginatedItems={props.AddNedDrugOrderItemSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title="Search"
            name="SEARCH"
            size="medium"
          />
        }
        saveButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSave}
            disabled={handleDisabledSave}
            // data
            paramKey={`${CARD_NAME}_SAVE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SAVE`]}
            // config
            color="green"
            title="Save"
            name="SAVE"
            size="medium"
          />
        }
      />
    </>
  );
};

CardAddNedDrugOrderItem.displayName = "CardAddNedDrugOrderItem";

export default React.memo(CardAddNedDrugOrderItem);

const tableHeaders: TableHeaderType<DrugOrderItemNedSerializer>[] = [
  {
    Header: "Date",
    accessor: "create_date",
    width: 150,
  },
  {
    Header: "HN",
    accessor: "hn",
  },
  {
    Header: "Order Code",
    accessor: "order_code",
  },
  {
    Header: "Type",
    accessor: "order_type",
  },
  {
    Header: "Drug Code",
    accessor: "drug_code",
  },
  {
    Header: "Drug Name",
    accessor: "drug_name",
    width: 200,
  },
  {
    Header: "NED",
    accessor: "ned",
    width: 400,
  },
];